import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Building2,
  Phone,
  Mail,
  Lock,
  CreditCard,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Star,
  CornerDownLeft,
  Clock,
  Globe,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/app/LanguageSwitcher";
import { Languages, MapPin } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const InlineMessage = ({ tone, children, testid }) => {
  if (!children) return null;
  const Icon = tone === "error" ? AlertCircle : CheckCircle2;
  const cls =
    tone === "error"
      ? "text-red-700 bg-red-50 border-red-100"
      : "text-green-800 bg-green-50 border-green-100";
  return (
    <p
      className={`mt-3 flex items-start gap-2 text-sm border rounded-md px-3 py-2 ${cls}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
};

const SectionCard = ({ icon: Icon, title, description, children, testid, className = "" }) => (
  <section
    className={`rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden ${className}`}
    data-testid={testid}
  >
    <header className="px-6 sm:px-7 py-5 border-b border-gray-100 flex items-start gap-4">
      <span className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-green-800" />
      </span>
      <div>
        <h2 className="font-display text-lg font-semibold text-gray-900 tracking-tight">{title}</h2>
        {description ? (
          <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{description}</p>
        ) : null}
      </div>
    </header>
    <div className="px-6 sm:px-7 py-6">{children}</div>
  </section>
);

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser, refreshMe } = useAuth();
  const { t } = useT();

  // Business profile state
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  // SMS consent (Twilio A2P 10DLC / Toll-Free verification). The checkbox
  // is intentionally UNCHECKED on initial render for new users — it only
  // becomes pre-checked when the server already has a stored consent on
  // the user's account (hydrated from /auth/me below).
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState("");
  const [googleReviewLink, setGoogleReviewLink] = useState("");
  const [customerReplyEmail, setCustomerReplyEmail] = useState("");
  const [tzValue, setTzValue] = useState("America/New_York");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ tone: "", text: "" });

  // Password state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ tone: "", text: "" });

  // Weekly Performance Email preference (default ON). Saves immediately.
  const [weeklyEmail, setWeeklyEmail] = useState(true);
  // Optional onboarding/setup-tip emails preference (default ON).
  const [lifecycleEmail, setLifecycleEmail] = useState(true);

  const toggleLifecycleEmail = async (checked) => {
    setLifecycleEmail(checked);
    try {
      const { data } = await api.patch("/auth/me", {
        lifecycle_emails_enabled: checked,
      });
      setUser(data);
      toast.success(t("settings.weeklyEmailSaved"));
    } catch {
      setLifecycleEmail(!checked);
      toast.error(t("settings.saveError") || "Could not save.");
    }
  };

  // Deep link from the Dashboard marketplace card (/settings#find-a-cleaner):
  // scroll the Find a Cleaner section into view. Waits for the user data to
  // hydrate (the page's only async layout dependency) so content above the
  // target has settled, then scrolls once per navigation (location.key).
  // The sticky-header gap is handled by scroll-mt on the target card.
  const location = useLocation();
  const fcScrolledRef = useRef(false);
  useEffect(() => {
    fcScrolledRef.current = false;
  }, [location.key]);
  useEffect(() => {
    if (location.hash !== "#find-a-cleaner" || fcScrolledRef.current || !user) return;
    fcScrolledRef.current = true;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        document
          .querySelector('[data-testid="settings-find-cleaner-card"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      })
    );
  }, [location.hash, location.key, user]);

  // Find a Cleaner marketplace enrollment (Phase 2).
  const [fcZip, setFcZip] = useState("");
  const [fcRadius, setFcRadius] = useState("");
  const [fcEnabled, setFcEnabled] = useState(false);
  const [fcSaving, setFcSaving] = useState(false);
  const [fcError, setFcError] = useState("");
  const saveMarketplace = async (e) => {
    e.preventDefault();
    setFcError("");
    if (fcZip && !/^\d{5}$/.test(fcZip)) {
      setFcError(t("settings.fcZipInvalid"));
      return;
    }
    if (fcEnabled && (!/^\d{5}$/.test(fcZip) || !fcRadius)) {
      setFcError(t("settings.fcNeedZipRadius"));
      return;
    }
    setFcSaving(true);
    try {
      const payload = {
        business_zip: fcZip,
        marketplace_enabled: fcEnabled,
      };
      if (fcRadius) payload.service_radius_miles = parseInt(fcRadius, 10);
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      toast.success(t("settings.fcSaved"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFcError(typeof detail === "string" ? detail : t("settings.fcZipInvalid"));
    } finally {
      setFcSaving(false);
    }
  };
  const toggleWeeklyEmail = async (checked) => {
    setWeeklyEmail(checked);
    try {
      const { data } = await api.patch("/auth/me", {
        weekly_performance_email_enabled: checked,
      });
      setUser(data);
      toast.success(t("settings.weeklyEmailSaved"));
    } catch {
      setWeeklyEmail(!checked);
      toast.error(t("settings.saveError") || "Could not save.");
    }
  };

  // Hydrate form fields from auth context
  useEffect(() => {
    if (user) {
      setBusinessName(user.business_name || "");
      setPhone(user.phone || "");
      // Hydrate consent from the server. New users with no phone start
      // unchecked (the explicit consent box is not pre-selected).
      setSmsConsent(Boolean(user.sms_consent));
      setGoogleReviewLink(user.google_review_link || "");
      setCustomerReplyEmail(user.customer_reply_email || "");
      setTzValue(user.timezone || "America/New_York");
      setWeeklyEmail(user.weekly_performance_email_enabled !== false);
      setLifecycleEmail(user.lifecycle_emails_enabled !== false);
      setFcZip(user.business_zip || "");
      setFcRadius(user.service_radius_miles ? String(user.service_radius_miles) : "");
      setFcEnabled(user.marketplace_enabled === true);
    }
  }, [user]);

  // Pull a fresh /auth/me on mount so the form reflects the canonical server state.
  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg({ tone: "", text: "" });
    setSmsConsentError("");
    const trimmedName = businessName.trim();
    if (!trimmedName) {
      setProfileMsg({ tone: "error", text: t("settings.businessNameEmpty") });
      return;
    }
    // Twilio A2P 10DLC: a phone number cannot be saved without explicit
    // SMS consent. Block client-side so the user gets immediate feedback
    // — the backend re-validates this server-side too.
    const trimmedPhone = phone.trim();
    if (trimmedPhone && !smsConsent) {
      setSmsConsentError(t("settings.smsConsentRequired"));
      toast.error(t("settings.smsConsentRequired"));
      return;
    }
    setProfileLoading(true);
    try {
      const trimmedLink = googleReviewLink.trim();
      if (trimmedLink && !/^https?:\/\//i.test(trimmedLink)) {
        setProfileMsg({ tone: "error", text: t("settings.invalidUrl") });
        setProfileLoading(false);
        return;
      }
      const trimmedReply = customerReplyEmail.trim();
      if (trimmedReply && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedReply)) {
        setProfileMsg({ tone: "error", text: t("settings.invalidEmail") });
        setProfileLoading(false);
        return;
      }
      const { data } = await api.patch("/auth/me", {
        business_name: trimmedName,
        phone: trimmedPhone,
        // Send both the checkbox state AND the exact disclosure text the
        // user saw, so the backend can record a tamper-evident proof of
        // consent. Always send `false` when phone is empty so the backend
        // wipes any stale consent on phone-clear.
        sms_consent: trimmedPhone ? Boolean(smsConsent) : false,
        sms_consent_text: trimmedPhone && smsConsent ? t("settings.smsConsentCheckboxLabel") : null,
        google_review_link: trimmedLink,
        customer_reply_email: trimmedReply,
        timezone: tzValue,
      });
      // Sync the local auth context so the rest of the app (Navbar email, etc.)
      // sees the new business_name immediately, no full refresh required.
      setUser(data);
      // Re-sync the checkbox from the canonical server response. When the
      // phone was cleared, the backend wiped consent → reflect that here.
      setSmsConsent(Boolean(data?.sms_consent));
      try {
        localStorage.setItem("tasklio_user", JSON.stringify(data));
      } catch {
        // ignore storage errors (private mode, quota, etc.) — server is the truth.
      }
      setProfileMsg({ tone: "success", text: t("settings.profileUpdated") });
      toast.success(t("settings.profileUpdated"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Surface backend SMS-consent rejection inline next to the
      // checkbox, in addition to the generic error banner.
      if (typeof detail === "string" && /sms consent/i.test(detail)) {
        setSmsConsentError(detail);
      }
      setProfileMsg({
        tone: "error",
        text: typeof detail === "string" ? detail : t("settings.profileUpdateError"),
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ tone: "", text: "" });
    if (newPw.length < 6) {
      setPwMsg({ tone: "error", text: t("settings.passwordMin") });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ tone: "error", text: t("settings.passwordMismatch") });
      return;
    }
    if (newPw === currentPw) {
      setPwMsg({ tone: "error", text: t("settings.passwordSame") });
      return;
    }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setPwMsg({ tone: "success", text: t("settings.passwordUpdated") });
      toast.success(t("settings.passwordUpdatedToast"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPwMsg({
        tone: "error",
        text: typeof detail === "string" ? detail : t("settings.passwordUpdateError"),
      });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="settings-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="settings-title">
            {t("settings.title")}
          </h1>
          <p className="mt-2 text-gray-500">
            {t("settings.subtitle")}
          </p>
        </div>

        <div className="space-y-6">
          {/* Preferences — App language */}
          <SectionCard
            icon={Languages}
            title={t("settings.preferences")}
            description={t("settings.preferencesHelp")}
            testid="settings-preferences-card"
          >
            <div>
              <Label className="mb-2 block">{t("lang.appLanguage")}</Label>
              <LanguageSwitcher variant="wide" testidPrefix="settings-lang-switch" />
            </div>
            <div className="mt-6 pt-5 border-t border-gray-100 flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="settings-weekly-email-toggle" className="block">
                  {t("settings.weeklyEmailTitle")}
                </Label>
                <p className="mt-1 text-sm text-gray-500">{t("settings.weeklyEmailHelp")}</p>
              </div>
              <Switch
                id="settings-weekly-email-toggle"
                checked={weeklyEmail}
                onCheckedChange={toggleWeeklyEmail}
                data-testid="settings-weekly-email-toggle"
              />
            </div>
            <div className="mt-5 pt-5 border-t border-gray-100 flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="settings-lifecycle-email-toggle" className="block">
                  {t("settings.lifecycleEmailTitle")}
                </Label>
                <p className="mt-1 text-sm text-gray-500">{t("settings.lifecycleEmailHelp")}</p>
              </div>
              <Switch
                id="settings-lifecycle-email-toggle"
                checked={lifecycleEmail}
                onCheckedChange={toggleLifecycleEmail}
                data-testid="settings-lifecycle-email-toggle"
              />
            </div>
          </SectionCard>

          {/* A) Business profile */}
          <SectionCard
            icon={Building2}
            title={t("settings.businessProfile")}
            description={t("settings.businessProfileHelp")}
            testid="settings-business-card"
          >
            <form onSubmit={saveProfile} className="space-y-5">
              <div>
                <Label htmlFor="business_name">{t("settings.businessName")}</Label>
                <Input
                  id="business_name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Sparkle Cleaning Co."
                  required
                  className="mt-2 h-11"
                  data-testid="settings-business-name-input"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-gray-400" />
                  {t("settings.smsPhone")}
                </Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPhone(v);
                    // Auto-clear the consent checkbox when the phone is
                    // emptied — SMS is now disabled for the account and
                    // the consent state shouldn't linger visually.
                    if (!v.trim()) {
                      setSmsConsent(false);
                      setSmsConsentError("");
                    }
                  }}
                  placeholder="+1 555 123 4567"
                  className="mt-2 h-11"
                  data-testid="settings-phone-input"
                />
                {/* Twilio A2P 10DLC explicit opt-in checkbox. Renders
                    directly under the SMS phone field. The box is
                    UNCHECKED by default for users without a stored
                    consent — `setSmsConsent` only flips to `true` on
                    explicit user interaction (or hydration from the
                    server when a prior consent exists). */}
                <label
                  className={`mt-3 flex items-start gap-2.5 p-3 rounded-md border cursor-pointer transition-colors ${
                    smsConsentError
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                  data-testid="settings-sms-consent-block"
                >
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => {
                      setSmsConsent(e.target.checked);
                      if (e.target.checked) setSmsConsentError("");
                    }}
                    className={`mt-0.5 w-4 h-4 shrink-0 ${
                      smsConsentError ? "accent-red-600" : "accent-green-800"
                    }`}
                    data-testid="settings-sms-consent-checkbox"
                    aria-invalid={smsConsentError ? "true" : undefined}
                  />
                  <span
                    className="text-[13px] leading-relaxed text-gray-700"
                    data-testid="settings-sms-consent-label"
                  >
                    {t("settings.smsConsentCheckboxLabel")}
                  </span>
                </label>
                {smsConsentError && (
                  <p
                    className="mt-2 text-sm text-red-600"
                    data-testid="settings-sms-consent-error"
                  >
                    {smsConsentError}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500" data-testid="settings-sms-phone-helper">
                  {t("settings.smsPhoneHelper")}
                </p>
              </div>
              <div>
                <Label htmlFor="google_review_link" className="flex items-center gap-2">
                  <Star className="w-3.5 h-3.5 text-gray-400" />
                  {t("settings.googleReviewLink")}
                </Label>
                <Input
                  id="google_review_link"
                  type="url"
                  inputMode="url"
                  value={googleReviewLink}
                  onChange={(e) => setGoogleReviewLink(e.target.value)}
                  placeholder="https://g.page/r/xxxxx/review"
                  className="mt-2 h-11"
                  data-testid="settings-google-review-link-input"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  {t("settings.googleReviewHelper")}
                </p>
              </div>
              <div>
                <Label htmlFor="customer_reply_email" className="flex items-center gap-2">
                  <CornerDownLeft className="w-3.5 h-3.5 text-gray-400" />
                  {t("settings.customerReplyEmail")}
                </Label>
                <Input
                  id="customer_reply_email"
                  type="email"
                  inputMode="email"
                  value={customerReplyEmail}
                  onChange={(e) => setCustomerReplyEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="mt-2 h-11"
                  data-testid="settings-customer-reply-email-input"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  {t("settings.customerReplyHelper")}
                </p>
                {(() => {
                  // Mirrors the backend's _resolve_customer_reply_to fallback
                  // chain so the owner sees exactly which address is currently
                  // active. Keep this in sync with that helper.
                  const effective =
                    (customerReplyEmail || "").trim() ||
                    (user?.email || "").trim() ||
                    "support@tasklio.co";
                  return (
                    <p
                      className="mt-1.5 text-xs text-gray-500"
                      data-testid="settings-customer-reply-effective"
                    >
                      {t("settings.customerReplyCurrent")}{" "}
                      <span className="font-medium text-gray-700">{effective}</span>
                    </p>
                  );
                })()}
              </div>
              <div>
                <Label htmlFor="timezone" className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  {t("settings.timezone")}
                </Label>
                <select
                  id="timezone"
                  value={tzValue}
                  onChange={(e) => setTzValue(e.target.value)}
                  className="mt-2 h-11 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
                  data-testid="settings-timezone-select"
                >
                  <option value="America/New_York">Eastern — America/New_York</option>
                  <option value="America/Chicago">Central — America/Chicago</option>
                  <option value="America/Denver">Mountain — America/Denver</option>
                  <option value="America/Phoenix">Arizona (no DST) — America/Phoenix</option>
                  <option value="America/Los_Angeles">Pacific — America/Los_Angeles</option>
                  <option value="America/Anchorage">Alaska — America/Anchorage</option>
                  <option value="Pacific/Honolulu">Hawaii — Pacific/Honolulu</option>
                </select>
                <p className="mt-1.5 text-xs text-gray-500" data-testid="settings-timezone-helper">
                  {t("settings.timezoneHelper")}
                </p>
              </div>

              <InlineMessage tone={profileMsg.tone} testid="settings-profile-message">
                {profileMsg.text}
              </InlineMessage>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={profileLoading}
                  className="h-11 px-6 bg-green-800 hover:bg-green-900 text-white font-semibold"
                  data-testid="settings-profile-save-button"
                >
                  {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.saveChanges")}
                </Button>
              </div>
            </form>
          </SectionCard>

          {/* A2) Customer Booking Page customization shortcut — the full
              editor lives on its own page (/booking-page-settings). */}
          <SectionCard
            icon={Globe}
            title={t("settings.bookingPage")}
            description={t("settings.bookingPageHelp")}
            testid="settings-booking-page-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-700">{t("settings.bookingPageHint")}</p>
              <Button
                onClick={() => navigate("/booking-page-settings")}
                className="h-11 bg-green-800 hover:bg-green-900 text-white font-semibold sm:px-5"
                data-testid="settings-booking-page-button"
              >
                {t("settings.bookingPageButton")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </SectionCard>

          {/* A3) Find a Cleaner marketplace enrollment (opt-in, default OFF) */}
          <SectionCard
            icon={MapPin}
            title={t("settings.fcTitle")}
            description={t("settings.fcHelp")}
            testid="settings-find-cleaner-card"
            className="scroll-mt-24"
          >
            <form onSubmit={saveMarketplace} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fc-zip">{t("settings.fcZipLabel")}</Label>
                  <Input
                    id="fc-zip"
                    value={fcZip}
                    onChange={(e) => setFcZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                    placeholder="30309"
                    inputMode="numeric"
                    maxLength={5}
                    className="mt-2 h-11"
                    data-testid="settings-fc-zip-input"
                  />
                </div>
                <div>
                  <Label htmlFor="fc-radius">{t("settings.fcRadiusLabel")}</Label>
                  <select
                    id="fc-radius"
                    value={fcRadius}
                    onChange={(e) => setFcRadius(e.target.value)}
                    className="mt-2 h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="settings-fc-radius-select"
                  >
                    <option value="">{t("settings.fcRadiusPlaceholder")}</option>
                    <option value="20">{t("settings.fcRadius20")}</option>
                    <option value="30">{t("settings.fcRadius30")}</option>
                  </select>
                </div>
              </div>
              <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-gray-200 bg-gray-50">
                <div>
                  <Label htmlFor="settings-fc-toggle" className="block">
                    {t("settings.fcToggleLabel")}
                  </Label>
                  <p className="mt-1 text-sm text-gray-500">{t("settings.fcToggleHelp")}</p>
                </div>
                <Switch
                  id="settings-fc-toggle"
                  checked={fcEnabled}
                  onCheckedChange={setFcEnabled}
                  data-testid="settings-fc-toggle"
                />
              </div>
              {fcError && (
                <p className="text-sm text-red-600" data-testid="settings-fc-error">
                  {fcError}
                </p>
              )}
              <Button
                type="submit"
                disabled={fcSaving}
                className="h-11 bg-green-800 hover:bg-green-900 text-white font-semibold px-6"
                data-testid="settings-fc-save-button"
              >
                {fcSaving ? "…" : t("common.save")}
              </Button>
            </form>
          </SectionCard>

          {/* B) Account (read-only email) */}
          <SectionCard
            icon={Mail}
            title={t("settings.account")}
            description={t("settings.accountHelp")}
            testid="settings-account-card"
          >
            <div>
              <Label htmlFor="email">{t("settings.email")}</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ""}
                readOnly
                disabled
                className="mt-2 h-11 bg-gray-50 cursor-not-allowed"
                data-testid="settings-email-input"
              />
              <p className="mt-1.5 text-xs text-gray-500" data-testid="settings-email-help">
                {t("settings.emailContactNote")} <a href="mailto:support@tasklio.co" className="text-green-800 hover:underline">support@tasklio.co</a>
              </p>
            </div>
          </SectionCard>

          {/* C) Change password */}
          <SectionCard
            icon={Lock}
            title={t("settings.changePassword")}
            description={t("settings.changePasswordHelp")}
            testid="settings-password-card"
          >
            <form onSubmit={savePassword} className="space-y-5" autoComplete="off">
              <div>
                <Label htmlFor="current_password">{t("settings.currentPassword")}</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="mt-2 h-11"
                  data-testid="settings-current-password-input"
                />
              </div>
              <div>
                <Label htmlFor="new_password">{t("settings.newPassword")}</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  placeholder={t("settings.passwordMinLength")}
                  className="mt-2 h-11"
                  data-testid="settings-new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">{t("settings.confirmPassword")}</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="mt-2 h-11"
                  data-testid="settings-confirm-password-input"
                />
              </div>

              <InlineMessage tone={pwMsg.tone} testid="settings-password-message">
                {pwMsg.text}
              </InlineMessage>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={pwLoading}
                  className="h-11 px-6 bg-green-800 hover:bg-green-900 text-white font-semibold"
                  data-testid="settings-password-save-button"
                >
                  {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("settings.updatePassword")}
                </Button>
              </div>
            </form>
          </SectionCard>

          {/* D) Billing shortcut */}
          <SectionCard
            icon={CreditCard}
            title={t("settings.billing")}
            description={t("settings.billingHelp")}
            testid="settings-billing-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm text-gray-700">
                  {user?.subscription_status === "active"
                    ? t("settings.billingOnPro")
                    : t("settings.billingOpenHint")}
                </p>
              </div>
              <Button
                onClick={() => navigate("/pricing#pricing")}
                className="h-11 bg-green-800 hover:bg-green-900 text-white font-semibold sm:px-5"
                data-testid="settings-manage-billing-button"
              >
                {t("settings.manageBilling")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </SectionCard>
        </div>

        <p className="mt-10 text-center text-sm text-gray-400">
          <Link to="/dashboard" className="hover:underline" data-testid="settings-back-to-dashboard">
            {t("settings.backToDashboard")}
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
